import * as echarts from 'echarts';

import { faGlobe, faInfoCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactECharts from "echarts-for-react";
import Markdown from "markdown-to-jsx";
import { Component, useEffect, useState } from "react";
import { Card, Container, Row, Alert } from "react-bootstrap";
import { useRef } from 'react';
const EChartsComponent = ({options}) => {
  delete options["title"]
  if (!options.toolbox) {
    options.toolbox = {
      show: true,
      feature: {
        saveAsImage: { show: true, title: 'Download', pixelRatio: 2 },
      }
    };
  }
  const chartRef = useRef(null);
  let chartInstance = null;
  // Initialize chart
  useEffect(() => {
    const option = {
      title: {
        text: 'ECharts Basic Example'
      },
      tooltip: {},
      xAxis: {
        data: ['Shirt', 'Cardigan', 'Chiffon', 'Pants', 'Heels', 'Socks']
      },
      yAxis: {},
      series: [{
        name: 'Sales',
        type: 'bar',
        data: [5, 20, 36, 10, 10, 20]
      }]
    };
    // Initialize chart instance
    if (chartRef.current) {
      chartInstance = echarts.init(chartRef.current, "dark");
      
      // Set options
      chartInstance.setOption(options);
      
      // Handle resize
      function handleResize() {
        chartInstance.resize();
      }
      
      window.addEventListener('resize', handleResize);
      
      // Clean up on component unmount
      return () => {
        chartInstance.dispose();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return (
    <div>
      <div ref={chartRef} style={{ color: "#d9d9d9", width: '100%', height: '400px' }} />
    </div>
  );
};

// Enhanced Error Boundary Component
class ChartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart Rendering Error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="danger" className="my-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Chart Error</strong>
          <p className="mt-2 mb-1">
            Something went wrong while rendering the chart.
          </p>
          {this.props.description && (
            <p className="mb-1">
              <strong>Chart description:</strong> {this.props.description}
            </p>
          )}
          {process.env.NODE_ENV === "development" && this.state.error && (
            <details className="mt-2">
              <summary>Error details (visible in development only)</summary>
              <pre
                className="mt-2 text-danger"
                style={{ fontSize: "0.8rem", overflowX: "auto" }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button onClick={this.resetError} className="btn btn-primary mt-2">
            Retry
          </button>
        </Alert>
      );
    }

    try {
      return this.props.children; // ⬅️ Wrap entire render inside try-catch
    } catch (error) {
      console.error("Unhandled Chart Error:", error);
      return (
        <Alert variant="danger" className="my-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Chart Error</strong>
          <p>Something went wrong while rendering the chart.</p>
          <button onClick={this.resetError} className="btn btn-primary mt-2">
            Retry
          </button>
        </Alert>
      );
    }
  }
}


// Safe Chart Component wrapper
const SafeChart = ({ config, description, style }) => {
  const [isValid, setIsValid] = useState(true);

  // Additional validation before rendering
  useEffect(() => {
    try {
      // Basic validation checks
      if (!config || typeof config !== 'object') {
        console.error("Invalid chart configuration:", config);
        setIsValid(false);
        return;
      }
      
      // Validate essential properties (add more as needed)
      if (config.series && !Array.isArray(config.series)) {
        console.error("Invalid series configuration:", config.series);
        setIsValid(false);
        return;
      }
      
      setIsValid(true);
    } catch (error) {
      console.error("Error validating chart config:", error);
      setIsValid(false);
    }
  }, [config]);

  if (!isValid) {
    return (
      <Alert variant="warning" className="my-2">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Invalid chart configuration
        {description && <p className="mt-2 mb-0">Chart description: {description}</p>}
      </Alert>
    );
  }

  return (
    // <ReactECharts
    //   theme={"dark"}
    //   option={{...config, toolbox: {
    //     show : true,
    //     feature : {
    //         mark : {show: true},
    //         dataView : {show: true, readOnly: false},
    //         saveAsImage : {show: true}
    //     }
    // }}}
    //   style={style || {
    //     zoom: "79%",
    //     height: "400px",
    //     color: "#d9d9d9",
    //     fontSize: "1em",
    //   }}
    //   // opts={{ renderer: 'canvas' }} // Using canvas renderer for better stability
    //   onEvents={{
    //     error: (e) => {
    //       console.error("ECharts error event:", e);
    //     }
    //   }}
    // />
    <EChartsComponent options={config} />
  );
};

const ChartComponent = ({ charts, summary, citations, error }) => {
  return (
    <>
      <Card
        className="shadow-lg"
        style={{
          border: "none",
          borderRadius: "12px",
          background:
            "linear-gradient(to right bottom, #1a121f, #171321, #121423, #0d1624, #061725)",
          color: "#d9d9d9",
        }}
      >
        <Card.Header>
          <h6 style={{ fontFamily: "Georgia, sans-serif" }}>
            <FontAwesomeIcon icon={faGlobe} /> Research Results
          </h6>
        </Card.Header>
        <Card.Body>
          <Container
          fluid
            style={{
              borderRadius: "10px",
              marginBottom: "20px",
              paddingBottom: "10px",
            }}
          >
            {charts && charts.length > 0 ? (
              charts.map((c, i) => {
                return c.chart_config ? (
                  <div key={`chart-${i}`}>
                    <p>
                      <FontAwesomeIcon icon={faInfoCircle} /> {c.description}
                    </p>
                    <ChartErrorBoundary description={c.description}>
                      <SafeChart 
                        config={c.chart_config} 
                        description={c.description}
                      />
                    </ChartErrorBoundary>
                  </div>
                ) : null;
              })
            ) : (
              <Alert variant="warning">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                No charts available or there was an issue with chart configuration
              </Alert>
            )}

            {summary ? (
              <Markdown options={{ forceBlock: true }}>{summary}</Markdown>
            ) : (
              <Alert variant="info">No summary available</Alert>
            )}
          </Container>
        </Card.Body>
      </Card>
      <br />
      <Row>
        <Container>
          {citations && citations.length > 0 ? (
            <Card.Text>
              <h6>Sources:</h6>
              <ol>
                {citations.map((c, i) => {
                  return (
                    <li key={i.toString()}>
                      <img
                        height="16"
                        width="16"
                        src={`http://www.google.com/s2/favicons?domain=${c}`}
                        alt="favicon"
                      />
                      <a
                        style={{
                          fontSize: "0.8em",
                          color: "#bfbfbd",
                          margin: "2px 2px",
                        }}
                        href={c}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {c}
                      </a>
                      <br />
                    </li>
                  );
                })}
              </ol>
            </Card.Text>
          ) : null}
        </Container>
      </Row>
    </>
  );
};

export default ChartComponent;